import {
  Button,
  DialogPositioner,
  Flex,
  HStack,
  IconButton,
  Separator,
  Text,
  UseDisclosureReturn,
} from '@chakra-ui/react';
import { IoClose } from 'react-icons/io5';
import { MdOutlineQuestionMark } from 'react-icons/md';
import { CreditForm } from '~/components/orders/payments/CreditForm';
import {
  DialogBackdrop,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTrigger,
} from '~/components/ui/dialog';
import { Tooltip } from '~/components/ui/tooltip';
import { useI18nLn } from '~/hooks/store';
import { CreditFormValue } from '~/types/components';

type Props = {
  disclosure: UseDisclosureReturn;
  onSubmit: (data: CreditFormValue) => Promise<void>;
  submitting: boolean;
  submitErrorText?: string;
  submitErrorTips?: string;
};

export const NewCreditModal: React.FC<Props> = (props: Props) => {
  const { t } = useI18nLn();
  const NEW_CREDIT_CARD_FORM_ID = 'new_credit_card_form';

  return (
    <DialogRoot
      size="sm"
      open={props.disclosure.open}
      scrollBehavior="inside"
      closeOnInteractOutside={true}
      onInteractOutside={props.disclosure.onClose}
    >
      <DialogPositioner>
        <DialogBackdrop />
        <DialogTrigger />
        <DialogContent>
          <DialogHeader maxH="100px" p={3} justifyContent="center">
            <Flex w="full" justify="space-between" alignItems="center">
              <Text
                fontSize="20px"
                fontWeight="bold"
                wordBreak="keep-all"
                overflowWrap="break-word"
              >
                {t('pages.buy.payments.credit.register_card')}
              </Text>
              <IconButton
                rounded="full"
                size="xs"
                bgColor="white"
                color="primary"
                borderWidth="2px"
                borderColor="primary"
                onClick={props.disclosure.onClose}
                _hover={{
                  bgColor: 'primary',
                  color: 'white',
                }}
              >
                <IoClose />
              </IconButton>
            </Flex>
          </DialogHeader>
          <DialogBody pt={0}>
            <CreditForm
              onSubmit={props.onSubmit}
              formId={NEW_CREDIT_CARD_FORM_ID}
            />
          </DialogBody>
          <Separator />
          <DialogFooter
            flexDir="column"
            mt={props.submitErrorText ? '0px' : '36px'}
            mb={0}
          >
            <Text
              color="red"
              fontSize="sm"
              mb="8px"
              hidden={!props.submitErrorText}
            >
              {props.submitErrorText}
            </Text>
            <HStack
              w="full"
              alignItems="stretch"
              justify="center"
              hidden={!props.submitErrorTips}
            >
              <Text color="red" fontSize="sm" mb="8px">
                {props.submitErrorTips}
              </Text>
              <Tooltip
                content="与信枠や残高の不足により、カード登録ができない場合がございます。"
                positioning={{
                  placement: 'right',
                }}
                closeOnClick={false}
                closeDelay={5000}
              >
                <MdOutlineQuestionMark />
              </Tooltip>
            </HStack>
            <Button
              type="submit"
              form={NEW_CREDIT_CARD_FORM_ID}
              variant="solid"
              bgColor="primary"
              w="full"
              mt="8px"
              loading={props.submitting}
              _hover={{
                filter: 'brightness(0.9)',
              }}
              _active={{
                filter: 'brightness(0.4)',
              }}
            >
              {t('global.common.register')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  );
};
